/**
 * performance.js — Advanced Performance Monitoring Library
 *
 * Monitoring dimensions:
 * 1. Core Web Vitals (LCP, FID/INP, CLS)
 * 2. Resource load time statistics by type
 * 3. Long task monitoring (Long Tasks API)
 * 4. Frame rate monitoring (FPS)
 * 5. Memory usage monitoring
 * 6. Navigation timing
 * 7. Custom performance marks and measurements
 * 8. Batched performance data reporting
 *
 * Usage:
 * import { initPerformanceMonitor, perfMark, perfMeasure } from '@/lib/performance';
 * initPerformanceMonitor();
 * perfMark('page-start');
 * perfMeasure('page-load', 'page-start');
 */

// ═══ Configuration ═══
const CONFIG = {
  sampleRate: 1.0,                 // Sampling rate (1.0 = 100%)
  reportInterval: 30000,            // Report interval (30s)
  maxBufferSize: 100,               // Max buffer size
  enableLongTask: true,             // Enable long task monitoring
  enableFPS: true,                  // Enable FPS monitoring
  enableMemory: true,               // Enable memory monitoring
  enableResource: true,             // Enable resource monitoring
};

// ═══ Global State ═══
const state = {
  metrics: [],
  marks: new Map(),
  isObserving: false,
  reportTimer: null,
  longTaskCount: 0,
};

// Frame rate tracking variables
let frameCount = 0;
let lastFrameTime = performance.now();
let fps = 60;

/**
 * Core Web Vitals thresholds
 */
export const THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 },    // ms
  FID: { good: 100, poor: 300 },      // ms
  INP: { good: 200, poor: 500 },      // ms
  CLS: { good: 0.1, poor: 0.25 },     // unitless
  FCP: { good: 1800, poor: 3000 },    // ms
  TTFB: { good: 800, poor: 1800 },    // ms
};

/**
 * Get rating for a given metric
 */
function getRating(name, value) {
  const threshold = THRESHOLDS[name];
  if (!threshold) return 'unknown';
  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
}

/**
 * Add metric to buffer
 */
function addMetric(name, value, rating, type = 'metric') {
  state.metrics.push({
    name,
    value: typeof value === 'number' ? Math.round(value * 100) / 100 : value,
    rating: rating || getRating(name, value),
    type,
    timestamp: Date.now(),
    url: window.location.href,
  });

  // Auto-report when buffer overflows
  if (state.metrics.length >= CONFIG.maxBufferSize) {
    flushMetrics();
  }

  // Development environment logging
  if (import.meta.env.DEV) {
    console.log(`[Perf] ${name}:`, name === 'CLS' ? value.toFixed(3) : `${Math.round(value)}ms`, `(${rating})`);
  }
}

// ═══════════════════════════════════════════════════════════
// 1. Core Web Vitals
// ═══════════════════════════════════════════════════════════

function observeWebVitals() {
  import('web-vitals').then(({ onCLS, onFID, onLCP, onFCP, onTTFB, onINP }) => {
    const report = (metric) => addMetric(metric.name, metric.value, getRating(metric.name, metric.value));
    onCLS(report);
    onFID(report);
    onLCP(report);
    onFCP(report);
    onTTFB(report);
    onINP(report);
  }).catch(() => {
    console.warn('[Perf] web-vitals import failed');
  });
}

// ═══════════════════════════════════════════════════════════
// 2. Long Task Monitoring
// ═══════════════════════════════════════════════════════════

function observeLongTasks() {
  if (!('PerformanceObserver' in window)) return;

  const supported = window.PerformanceObserver?.supportedEntryTypes;
  if (!supported?.includes('longtask')) {
    observeFrameDrops();
    return;
  }

  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        state.longTaskCount++;
        addMetric('LongTask', entry.duration, entry.duration > 100 ? 'poor' : 'needs-improvement', 'longtask');
      }
    });
    observer.observe({ type: 'longtask', buffered: true });
  } catch {
    observeFrameDrops();
  }
}

function observeFrameDrops() {
  const checkFrame = () => {
    frameCount++;
    const now = performance.now();
    const elapsed = now - lastFrameTime;

    if (elapsed >= 1000) {
      fps = Math.round((frameCount * 1000) / elapsed);
      if (fps < 30) {
        addMetric('FPS-Low', fps, fps < 15 ? 'poor' : 'needs-improvement', 'fps');
      }
      frameCount = 0;
      lastFrameTime = now;
    }

    if (state.isObserving) {
      requestAnimationFrame(checkFrame);
    }
  };
  requestAnimationFrame(checkFrame);
}

// ═══════════════════════════════════════════════════════════
// 3. Memory Monitoring
// ═══════════════════════════════════════════════════════════

function observeMemory() {
  if (!CONFIG.enableMemory) return;
  if (!performance.memory) return;

  const check = () => {
    const memory = performance.memory;
    const usedMB = Math.round(memory.usedJSHeapSize / 1024 / 1024);
    const usagePercent = memory.usedJSHeapSize / memory.jsHeapSizeLimit;

    addMetric('Memory-Used', usedMB, usagePercent > 0.8 ? 'poor' : usagePercent > 0.6 ? 'needs-improvement' : 'good', 'memory');
    addMetric('Memory-Total', Math.round(memory.totalJSHeapSize / 1024 / 1024), 'unknown', 'memory');
  };

  setInterval(check, 30000);
}

// ═══════════════════════════════════════════════════════════
// 4. Resource Loading Monitoring
// ═══════════════════════════════════════════════════════════

function observeResources() {
  if (!CONFIG.enableResource) return;
  if (!('PerformanceObserver' in window)) return;

  const resourceStats = {
    js: { count: 0, totalSize: 0, totalTime: 0 },
    css: { count: 0, totalSize: 0, totalTime: 0 },
    image: { count: 0, totalSize: 0, totalTime: 0 },
    font: { count: 0, totalSize: 0, totalTime: 0 },
    other: { count: 0, totalSize: 0, totalTime: 0 },
  };

  const getResourceType = (initiatorType, url) => {
    if (['script', 'js'].includes(initiatorType)) return 'js';
    if (['css', 'link'].includes(initiatorType) || url.endsWith('.css')) return 'css';
    if (['img', 'image', 'css-image'].includes(initiatorType) || /\.(?:png|jpe?g|gif|webp|avif|svg)/.test(url)) return 'image';
    if (['font'].includes(initiatorType) || /\.(?:woff2?|ttf|otf|eot)/.test(url)) return 'font';
    return 'other';
  };

  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType !== 'resource') continue;

        const type = getResourceType(entry.initiatorType, entry.name);
        const duration = entry.duration;
        const size = entry.transferSize || 0;

        resourceStats[type].count++;
        resourceStats[type].totalSize += size;
        resourceStats[type].totalTime += duration;

        if (duration > 1000) {
          addMetric('Slow-Resource', duration, 'poor', 'resource');
        }
      }
    });
    observer.observe({ type: 'resource', buffered: true });

    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        for (const [type, stats] of Object.entries(resourceStats)) {
          if (stats.count > 0) {
            addMetric(`Resource-${type}-Count`, stats.count, 'unknown', 'resource-summary');
            addMetric(`Resource-${type}-Size`, Math.round(stats.totalSize / 1024), 'unknown', 'resource-summary');
            addMetric(`Resource-${type}-AvgTime`, Math.round(stats.totalTime / stats.count), 'unknown', 'resource-summary');
          }
        }
      }
    });
  } catch {
    console.warn('[Perf] Resource observation not supported');
  }
}

// ═══════════════════════════════════════════════════════════
// 5. Navigation Timing
// ═══════════════════════════════════════════════════════════

function observeNavigation() {
  if (!('performance' in window)) return;

  window.addEventListener('load', () => {
    setTimeout(() => {
      const nav = performance.getEntriesByType('navigation')[0];
      if (!nav) return;

      addMetric('TTLB', nav.responseStart - nav.requestStart, getRating('TTFB', nav.responseStart - nav.requestStart), 'navigation');
      addMetric('DOM-Parse', nav.domInteractive - nav.responseEnd, nav.domInteractive - nav.responseEnd > 1000 ? 'poor' : 'good', 'navigation');
      addMetric('DOM-Ready', nav.domContentLoadedEventEnd - nav.requestStart, 'unknown', 'navigation');
      addMetric('Full-Load', nav.loadEventEnd - nav.requestStart, 'unknown', 'navigation');
    }, 0);
  });
}

// ═══════════════════════════════════════════════════════════
// 6. Data Reporting
// ═══════════════════════════════════════════════════════════

async function reportMetrics(metrics) {
  const endpoint = import.meta.env.VITE_METRICS_ENDPOINT;
  if (!endpoint) return;

  const payload = {
    metrics,
    metadata: {
      url: window.location.href,
      userAgent: navigator.userAgent,
      connection: navigator.connection?.effectiveType || 'unknown',
      deviceMemory: navigator.deviceMemory || 'unknown',
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      timestamp: Date.now(),
    },
  };

  if (navigator.sendBeacon) {
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    navigator.sendBeacon(endpoint, blob);
    return;
  }

  try {
    await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // Silent failure
  }
}

function flushMetrics() {
  if (state.metrics.length === 0) return;
  const batch = state.metrics.splice(0, CONFIG.maxBufferSize);
  reportMetrics(batch);
}

// ═══════════════════════════════════════════════════════════
// Public API
// ═══════════════════════════════════════════════════════════

/**
 * Initialize performance monitoring (call once in main.jsx)
 */
export function initPerformanceMonitor(options = {}) {
  Object.assign(CONFIG, options);

  if (Math.random() > CONFIG.sampleRate) return;
  if (state.isObserving) return;
  state.isObserving = true;

  observeWebVitals();
  observeLongTasks();
  observeMemory();
  observeResources();
  observeNavigation();

  state.reportTimer = setInterval(flushMetrics, CONFIG.reportInterval);

  window.addEventListener('beforeunload', flushMetrics);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushMetrics();
  });

  // Remove initial loader
  const loader = document.getElementById('initial-loader');
  if (loader) {
    loader.classList.add('hidden');
    setTimeout(() => loader.remove(), 500);
  }

  console.log('[Perf] Monitor initialized');
}

/**
 * Create a performance mark
 */
export function perfMark(name) {
  if (!performance.mark) return;
  performance.mark(name);
  state.marks.set(name, performance.now());
}

/**
 * Measure duration between two marks
 */
export function perfMeasure(name, startMark, endMark) {
  if (!performance.measure) return;
  try {
    if (endMark) {
      performance.measure(name, startMark, endMark);
    } else {
      performance.measure(name, startMark);
    }

    const entries = performance.getEntriesByName(name, 'measure');
    if (entries.length > 0) {
      const duration = entries[entries.length - 1].duration;
      addMetric(name, duration, duration > 1000 ? 'poor' : duration > 300 ? 'needs-improvement' : 'good', 'custom');

      if (performance.clearMeasures) {
        performance.clearMeasures(name);
      }
    }
  } catch (err) {
    console.warn('[Perf] Measure failed:', err);
  }
}

/**
 * Performance timer
 * @example
 * const end = perfTimer('expensive-operation');
 * await doSomething();
 * const duration = end();
 */
export function perfTimer(name) {
  const start = performance.now();
  return (logName = name) => {
    const duration = performance.now() - start;
    addMetric(logName, duration, duration > 1000 ? 'poor' : duration > 300 ? 'needs-improvement' : 'good', 'timer');
    return Math.round(duration * 100) / 100;
  };
}

/**
 * Wrap async function with performance tracking
 */
export function withPerfTracking(name, fn) {
  return async (...args) => {
    const end = perfTimer(name);
    try {
      const result = await fn(...args);
      end();
      return result;
    } catch (err) {
      end(`${name}-error`);
      throw err;
    }
  };
}

/**
 * Get current performance snapshot
 */
export function getPerfSnapshot() {
  return {
    metrics: [...state.metrics],
    longTaskCount: state.longTaskCount,
    fps,
    marks: Object.fromEntries(state.marks),
  };
}

/**
 * Destroy monitor
 */
export function destroyPerformanceMonitor() {
  state.isObserving = false;
  if (state.reportTimer) {
    clearInterval(state.reportTimer);
  }
  flushMetrics();
}

export default {
  initPerformanceMonitor,
  perfMark,
  perfMeasure,
  perfTimer,
  withPerfTracking,
  getPerfSnapshot,
  destroyPerformanceMonitor,
  THRESHOLDS,
};
